import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageSquare, Send } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    tvBrand: '',
    problem: '',
    area: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message
    const message = `Hello Garg Electronics,

I need TV repair service:

Name: ${formData.name}
Phone: ${formData.phone}
TV Brand: ${formData.tvBrand}
Problem: ${formData.problem}
Area: ${formData.area}

Please contact me for service.`;

    const whatsappUrl = `https://wa.me/918299034722?text=${encodeURIComponent(message)}`;
    
    // Google Ads Conversion Tracking
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-CONVERSION_ID/CONTACT_LABEL',
        'event_category': 'Contact',
        'event_label': 'Contact Form Submission',
        'value': 1.0,
        'currency': 'INR'
      });
    }
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Show success message
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({
        name: '',
        phone: '',
        tvBrand: '',
        problem: '',
        area: ''
      });
      setIsSubmitted(false);
    }, 3000);
  };

  const tvBrands = [
    'Samsung',
    'LG',
    'Sony',
    'OnePlus',
    'Xiaomi',
    'TCL',
    'Haier',
    'Panasonic',
    'Philips',
    'Onida',
    'Lloyd',
    'Other'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Contact Garg Electronics
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Get your TV fixed today! We're just one call away. Same-day service across Kanpur.
          </p>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a
              href="tel:9565711092"
              className="bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all group"
            >
              <Phone className="w-10 h-10 text-gray-900 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Call Primary</h3>
              <p className="text-2xl font-bold text-gray-900">9565711092</p>
            </a>

            <a
              href="tel:6387857396"
              className="bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all group"
            >
              <Phone className="w-10 h-10 text-gray-900 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Call Secondary</h3>
              <p className="text-2xl font-bold text-gray-900">6387857396</p>
            </a>

            <a
              href="https://wa.me/918299034722"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl shadow-md hover:shadow-xl transition-all group"
            >
              <MessageSquare className="w-10 h-10 text-green-600 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-lg font-semibold text-green-600">Click to Chat</p>
            </a>

            <a
              href="mailto:gargelectronic6387@gmail.com"
              className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md hover:shadow-xl transition-all group"
            >
              <Mail className="w-10 h-10 text-blue-600 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Email Us</h3>
              <p className="text-sm font-semibold text-blue-600 break-all">gargelectronic6387@gmail.com</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Book a Repair Service
                </h2>
                <p className="text-gray-600 mb-6">
                  Fill the form below and we'll call you back within 30 minutes!
                </p>

                {isSubmitted ? (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                    <div className="text-green-600 mb-4">
                      <Send className="w-16 h-16 mx-auto" />
                    </div>
                    <h3 className="text-2xl font-bold text-green-800 mb-2">Request Sent!</h3>
                    <p className="text-green-700">
                      Opening WhatsApp... We'll contact you shortly!
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                        placeholder="Enter your name"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        pattern="[0-9]{10}"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                        placeholder="10-digit mobile number"
                      />
                    </div>

                    <div>
                      <label htmlFor="tvBrand" className="block text-sm font-semibold text-gray-700 mb-2">
                        TV Brand *
                      </label>
                      <select
                        id="tvBrand"
                        name="tvBrand"
                        value={formData.tvBrand}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                      >
                        <option value="">Select TV Brand</option>
                        {tvBrands.map((brand) => (
                          <option key={brand} value={brand}>
                            {brand}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="problem" className="block text-sm font-semibold text-gray-700 mb-2">
                        Problem Description *
                      </label>
                      <textarea
                        id="problem"
                        name="problem"
                        value={formData.problem}
                        onChange={handleChange}
                        required
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors resize-none"
                        placeholder="Describe the TV problem (e.g., screen not turning on, no sound, etc.)"
                      ></textarea>
                    </div>

                    <div>
                      <label htmlFor="area" className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Area in Kanpur *
                      </label>
                      <input
                        type="text"
                        id="area"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                        placeholder="e.g., Nehru Nagar, Kalyanpur"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-purple-600 text-white font-bold py-4 rounded-lg hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl flex items-center justify-center"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Send Request via WhatsApp
                    </button>

                    <p className="text-sm text-gray-600 text-center">
                      By submitting, you agree to be contacted via phone/WhatsApp
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Visit Our Shop
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <MapPin className="w-6 h-6 text-teal-600 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Address</h3>
                      <p className="text-gray-700">
                        Garg Electronics<br />
                        107/208 Nehru Nagar<br />
                        Near Bali Ji Chowk<br />
                        Kanpur, Uttar Pradesh - 208012
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock className="w-6 h-6 text-teal-600 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Service Hours</h3>
                      <p className="text-gray-700">
                        9:00 AM - 8:00 PM<br />
                        All 7 Days (No holidays)<br />
                        <span className="text-teal-600 font-semibold">Same-Day Service Available</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Phone className="w-6 h-6 text-teal-600 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Phone</h3>
                      <p className="text-gray-700">
                        Primary: <a href="tel:9565711092" className="text-gray-900 hover:underline font-semibold">9565711092</a><br />
                        Secondary: <a href="tel:6387857396" className="text-gray-900 hover:underline font-semibold">6387857396</a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail className="w-6 h-6 text-gray-900 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                      <a
                        href="mailto:gargelectronic6387@gmail.com"
                        className="text-gray-900 hover:underline break-all"
                      >
                        gargelectronic6387@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Choose Us - Quick Points */}
              <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Why Choose Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">⚡</span>
                    <span><strong>Same-Day Repair:</strong> 90% cases fixed in 3 hours</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🛡️</span>
                    <span><strong>30+ Years Trust:</strong> 10,000+ happy customers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">✅</span>
                    <span><strong>Expert Technicians:</strong> Certified & verified</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">💰</span>
                    <span><strong>Transparent Pricing:</strong> No hidden charges</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🔧</span>
                    <span><strong>120-Day Warranty:</strong> On all repairs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Q: How fast is your service?</h3>
              <p className="text-gray-700">A: Same-day in 90% cases. Most repairs are completed within 3 hours at your doorstep.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Q: Do I need to bring my TV to the shop?</h3>
              <p className="text-gray-700">A: No! We provide complete doorstep service. Our technician will come to your home.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Q: What if the TV cannot be repaired?</h3>
              <p className="text-gray-700">A: You pay NO repair charges. Only ₹299 visitation fee applies for diagnosis.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Q: Are the parts original?</h3>
              <p className="text-gray-700">A: We use high-quality A-grade parts with 120-day warranty, sourced from trusted suppliers.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Q: Is diagnosis free?</h3>
              <p className="text-gray-700">A: Yes, diagnosis is completely free if you proceed with the repair.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Q: Do you work on Sundays?</h3>
              <p className="text-gray-700">A: Yes! We provide service all 7 days a week with no extra charges on weekends.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
